import { ref, provide, inject, type InjectionKey, type Ref } from 'vue'
import type { Title, ContentItem } from '@/types'
import { usePlayerStore } from '@/stores/player'
import { useLibraryStore } from '@/stores/library'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'
import { toast } from '@/utils/toast'
import { logger, createLogger } from '@/utils/logger'
import { getConfig } from '@/config'
import { injectLoginPopup } from '@/composables/useLoginPopup'
import { useShareModal } from '@/composables/useShareModal'

// Create a namespaced logger
const menuLogger = createLogger('ContentMenu')

// Helper function to safely log without breaking tests
function safeLog(logger: any, method: string, ...args: any[]) {
  // Don't log in test environment
  if (import.meta.env.MODE === 'test') return
  
  // Check if the logger and method exist before calling
  if (logger && typeof logger[method] === 'function') {
    logger[method](...args)
  }
}

type ContentMenuContext = {
  selectedContent: Ref<Title | null>
  forceShowRemoveFromLibrary: Ref<boolean>
  showContentMenu: (content: ContentItem, options?: { forceShowRemoveFromLibrary?: boolean }) => void
  handleContentAction: (action: string) => Promise<void>
}

const ContentMenuKey = Symbol('ContentMenu') as InjectionKey<ContentMenuContext>

// Create a global state outside of any component
const selectedContent = ref<Title | null>(null)
const forceShowRemoveFromLibrary = ref(false)

export function useContentMenu() {
  const playerStore = usePlayerStore()
  const libraryStore = useLibraryStore()
  const authStore = useAuthStore()
  const router = useRouter()
  const { showLoginPopup } = injectLoginPopup()

  const showContentMenu = (
    content: ContentItem,
    options?: { forceShowRemoveFromLibrary?: boolean }
  ) => {
    safeLog(menuLogger, 'log', 'showContentMenu called with:', content, options)
    // Normalize id from title_id (finished/sync payloads often only have title_id)
    const normalized = { ...content } as Title & { title_id?: string | number }
    if ((normalized.id === undefined || normalized.id === null || normalized.id === '') &&
        normalized.title_id !== undefined && normalized.title_id !== null && normalized.title_id !== '') {
      normalized.id = normalized.title_id as any
    }
    forceShowRemoveFromLibrary.value = options?.forceShowRemoveFromLibrary === true
    selectedContent.value = normalized
    safeLog(menuLogger, 'log', 'selectedContent after update:', selectedContent.value)
  }

  const handleContentAction = async (action: string) => {
    if (!selectedContent.value) return

    safeLog(menuLogger, 'log', `Handling action: ${action} for content:`, selectedContent.value)

    switch (action) {
      case 'play':
        safeLog(menuLogger, 'log', 'Playing from beginning')
        if (!selectedContent.value.chapters || selectedContent.value.chapters.length === 0) {
          safeLog(menuLogger, 'error', 'Cannot play content without chapters:', selectedContent.value)
          toast.error({
            title: 'Unable to play this content',
            message: 'Missing audio data.'
          })
          return
        }
        
        const firstChapter = selectedContent.value.chapters[0]
        if (!firstChapter.url || typeof firstChapter.url !== 'string') {
          safeLog(menuLogger, 'error', 'First chapter has invalid URL:', firstChapter)
          toast.error({
            title: 'Unable to play this content',
            message: 'Invalid audio source.'
          })
          return
        }
        
        safeLog(menuLogger, 'log', 'Playing content with first chapter URL:', firstChapter.url)
        
        playerStore.playFromBeginning(selectedContent.value)
        break
      case 'continue':
        safeLog(menuLogger, 'log', 'Continuing from last position')
        const config = getConfig()
        
        // Check if this is an EPUB title
        const isEpub = config.features.epubReader && (
          selectedContent.value.chapters?.some((ch: any) => ch.epubUrl) ||
          (selectedContent.value as any).contentFormat === 'epub' ||
          config.app.id === 'reading-plus'
        )
        
        if (isEpub) {
          // Navigate to EPUB reader - it will auto-resume
          const slug = selectedContent.value.slug || selectedContent.value.title.toLowerCase().replace(/\s+/g, '-')
          router.push({
            name: 'epub-reader',
            params: {
              id: String(selectedContent.value.id),
              slug: slug
            }
          })
        } else {
          // For audio, resume from last position
          if (!selectedContent.value.chapters || selectedContent.value.chapters.length === 0) {
            safeLog(menuLogger, 'error', 'Cannot play content without chapters:', selectedContent.value)
            toast.error({
              title: 'Unable to play this content',
              message: 'Missing audio data.'
            })
            return
          }
          
          const hasValidUrl = selectedContent.value.chapters.some(
            chapter => chapter.url && typeof chapter.url === 'string'
          )
          
          if (!hasValidUrl) {
            safeLog(menuLogger, 'error', 'No chapters have valid URLs:', selectedContent.value.chapters)
            toast.error({
              title: 'Unable to play this content',
              message: 'Invalid audio source.'
            })
            return
          }
          
          playerStore.playFromLastPosition(selectedContent.value)
        }
        break
      case 'favorite':
        safeLog(menuLogger, 'log', 'Toggling favorite')
        await libraryStore.toggleFavorite(selectedContent.value)
        break
      case 'share': {
        const { openShareModal } = useShareModal()
        const url = `${window.location.origin}${router.resolve(`/titles/${selectedContent.value.id}`).href}`
        openShareModal({
          title: selectedContent.value.title,
          text: selectedContent.value.description || '',
          url
        })
        break
      }
      case 'mark-as-finished':
        safeLog(menuLogger, 'log', 'Marking as finished')
        const success = await libraryStore.markTitleAsFinished(selectedContent.value)
        if (success) {
          toast.success({
            title: 'Marked as Finished',
            message: `Content has been marked as finished and added to your Finished collection.`
          })
        } else {
          toast.error({
            title: 'Error',
            message: 'Failed to mark the title as finished. Please try again.'
          })
        }
        break
      case 'bookmark':
        // Handle bookmark action
        break
      case 'subscribe':
        if (!authStore.isAuthenticated) {
          safeLog(menuLogger, 'log', 'Guest clicked subscribe, showing login popup')
          showLoginPopup({ context: 'subscribe' })
        } else {
          router.push('/subscribe')
        }
        break
      case 'signin':
        if (!authStore.isAuthenticated) {
          safeLog(menuLogger, 'log', 'Guest clicked sign in, showing login popup')
          showLoginPopup({ context: 'subscribe' })
        }
        break
      case 'purchase':
        if (selectedContent.value) {
          router.push(`/titles/${selectedContent.value.id}`)
        }
        break
      default:
        safeLog(menuLogger, 'warn', `Unknown action: ${action}`)
    }

    selectedContent.value = null
    forceShowRemoveFromLibrary.value = false
  }

  return {
    selectedContent,
    forceShowRemoveFromLibrary,
    showContentMenu,
    handleContentAction
  }
}

export function provideContentMenu() {
  const context = useContentMenu()
  provide(ContentMenuKey, context)
  return context
}

export function injectContentMenu() {
  const context = inject(ContentMenuKey)
  if (!context) {
    throw new Error('useContentMenu must be used within a component that has called provideContentMenu')
  }
  return context
} 